import React, {
    useEffect,
    useState,
    useMemo,
    useCallback,
} from 'react';
import PropTypes from 'prop-types';
import {BrickContext, Config} from 'olympe';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    CircularProgress,
    Typography, Chip,
} from '@mui/material';
import {
    getAuthorizationDataLambda,
    getPunchoutCatalogsInfoLambda,
    getPunchoutCatalogURLLambda,
} from '../../../../utils/functionSignatures';
import {LAMBDA_NAMES} from '../../../../utils/constants';
import {PaginationActions} from '../../../PaginationActions.jsx';
import {ErrorPopup} from '../ErrorPopup.jsx';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {ezprOrange} from '../../../_helpers';

/**
 * @param {PunchoutCatalogTable.propTypes} props
 * @return {JSX.Element}
 */
export function PunchoutCatalogTable(props) {
    const [catalogs, setCatalogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [fetchingURL, setFetchingURL] = useState(false);
    const [errorPopupOpen, setErrorPopupOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [companyCodeFilter, setCompanyCodeFilter] = useState(true);
    const [authList, setAuthList] = useState();

    const getPunchoutCatalogs = props.context.get(LAMBDA_NAMES.GET_PUNCHOUT_CATALOGS);
    const getPunchoutCatalogURL = props.context.get(LAMBDA_NAMES.GET_PUNCHOUT_CATALOG_URL);
    const getAuthorizationData = props.context.get(LAMBDA_NAMES.GET_AUTHORIZATION_DATA);

    useEffect(async () => {
        setLoading(true);
        setError(null);
        const currentUserId = localStorage.getItem('Current_Sap_User') || 'JDUBEY';
        console.log('[PunchOut] Fetching authorizations for user:', currentUserId);

        await getAuthorizationDataLambda(
            props.context,
            getAuthorizationData,
            currentUserId,
            async (authResult) => {
                console.log('[PunchOut] Authorization data received:', JSON.parse(authResult));
                setAuthList(JSON.parse(authResult));
                setLoading(false);
            },
            () => {
                console.error('[PunchOut] Failed to load authorizations for user:', currentUserId);
                console.log('failed to load authorizations');
                setLoading(false);
                setError('Unable to load authorizations.');
            });
    }, []);

    useEffect(async () => {
        if (!authList) {
            return; // Do not fetch the punchOut catalogs as long as the authorization is not fetched.
        }
        setLoading(true);
        setError(null);

        console.log('[PunchOut] Fetching catalogs with companyCode filter:', companyCodeFilter ? props.companyCode?.value : '(no filter)');
        getPunchoutCatalogsInfoLambda(props.context, getPunchoutCatalogs, companyCodeFilter ? props.companyCode.value : '')
            .then(([raw]) => {
                const env = Config.getParameter('ENV');
                const catalogs = JSON.parse(raw);
                console.log('[PunchOut] Raw catalogs from backend:', catalogs);
                let filtered = catalogs;
                if (env !== 'codev') {
                    const allCompanyCodeAuthorized = authList.some((a) => a.company_code === '*');
                    const allPurchaseOrgAuthorized = authList.some((a) => a.purchase_organization === '*');

                    filtered = catalogs.filter((catalog) => {
                        const authorizedData = authList.filter((auth) => {
                            if (!auth.activity) {
                                console.warn('[PunchOut] Auth entry missing activity field:', auth);
                            }
                            return (
                                (auth.company_code === catalog.company_code && auth.activity?.split(',').includes('03')) ||
                                (auth.purchase_organization === catalog.purchase_org && auth.activity?.split(',').includes('03'))
                            );
                        });
                        // TODO: Reverth the auth bypass after the DB is fixed
                        const allowed = (authorizedData.some((auth) => auth.company_code === catalog.company_code) && authorizedData.some((auth) => auth.purchase_organization === catalog.purchase_org)) || (allCompanyCodeAuthorized && allPurchaseOrgAuthorized);
                        console.log(`[PunchOut] Catalog ${catalog.vendor_code} (cc: ${catalog.company_code}, purchOrg: ${catalog.purchase_org}) → allowed: ${allowed}, matched auth entries: ${authorizedData.length}, wildcards: cc=${allCompanyCodeAuthorized}, pOrg=${allPurchaseOrgAuthorized}`);
                        return allowed;
                    });
                }
                console.log('[PunchOut] Catalogs after authorization filter:', filtered);

                setCatalogs(
                    filtered.map((v) => ({
                        companyCode: v.company_code,
                        purchaseOrg: v.purchase_org,
                        vendorCode: v.vendor_code,
                        vendorName: v.vendor_name,
                        fallbackUrl: v.url,
                    })),
                );
            })
            .catch((err) => {
                console.error('[PunchOut] Failed to load catalogs from backend:', err);
                console.error('Failed to load punch-out catalogs', err);
                setError('Unable to load catalogs.');
            })
            .finally(() => {
                setLoading(false);
            });
    }, [authList, companyCodeFilter]);

    const columns = useMemo(() => [
        {id: 'companyCode', label: 'Company Code'},
        {id: 'purchaseOrg', label: 'Purchase Org'},
        {id: 'vendorCode', label: 'Vendor Code'},
        {id: 'vendorName', label: 'Vendor Name'},
    ], []);

    const handleChangePage = useCallback((_, newPage) => {
        setPage(newPage);
    }, []);

    const handleChangeRowsPerPage = useCallback((e) => {
        setRowsPerPage(+e.target.value);
        setPage(0);
    }, []);

    const handleRowClick = useCallback((row) => {
        if (fetchingURL) return;
        setFetchingURL(true);

        getPunchoutCatalogURLLambda(
            props.context,
            getPunchoutCatalogURL,
            row.vendorCode,
            row.vendorName,
            row.companyCode,
            props.companyCode.label,
            props.purchaseOrg.label,
            props.DYNInfo?.plant || props.PRInfo?.plant,
            props.DYNInfo?.deliveryAddress || props.PRInfo?.deliveryAddress,
            props.DYNInfo?.materialCode || props.PRInfo?.materialCode,
            props.DYNInfo?.l3Category || props.PRInfo?.l3Category,
            props.EPRId,
            'punchout',
        )
            .then(([url]) => {
                window.open(url, '_blank')?.focus();
                props.onClosePunchOut(`${row.vendorCode} - ${row.vendorName}`);
            })
            .catch((err) => {
                console.error('Failed to get punch-out URL', err);
                setErrorMessage(`Failed to open catalog for supplier ${row.vendorCode} - ${row.vendorName}. Please try again or contact support.`);
                setErrorPopupOpen(true);
            })
            .finally(() => {
                setFetchingURL(false);
            });
    }, [
        fetchingURL,
        getPunchoutCatalogURL,
        props.companyCode.label,
        props.purchaseOrg.label,
        props.DYNInfo,
        props.PRInfo,
        props.EPRId,
        props.onClosePunchOut,
    ]);

    const handleClickOnCompanyCodeFilter = () => {
        setCompanyCodeFilter(!companyCodeFilter);
    };


    return (
        <>
            {props.companyCode && <Chip
                label={props.companyCode.label}
                icon={<CheckCircleIcon />}
                onClick={() => handleClickOnCompanyCodeFilter()}
                sx={{
                    'margin': '4px',
                    'color': companyCodeFilter ? ezprOrange : 'grey',
                    'borderColor': companyCodeFilter ? ezprOrange : 'grey',
                    'borderWidth': '1px',
                    'borderStyle': 'solid',
                    '& .MuiChip-icon': {
                        color: companyCodeFilter ? ezprOrange : 'grey',
                    },
                }}
                variant="outlined"
            />}
            <TableContainer
                sx={{
                    marginTop: '1rem',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    maxHeight: '400px',
                    overflowY: 'auto',
                    overscrollBehavior: 'contain',
                }}
                className={'popup-scroller'}>
                <Table stickyHeader aria-label="Punchout Catalogs">
                    <TableHead>
                        <TableRow>
                            {columns.map((col) => (
                                <TableCell
                                    key={col.id}
                                    scope="col"
                                    sx={{
                                        background: '#F7F7F7',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {col.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(loading || fetchingURL) && (
                            <TableRow>
                                <TableCell colSpan={columns.length} align="center">
                                    <CircularProgress size={24}/>
                                    {fetchingURL && <div style={{marginLeft: '8px'}}>Opening catalog...</div>}
                                </TableCell>
                            </TableRow>
                        )}
                        {error && (
                            <TableRow>
                                <TableCell colSpan={columns.length}>
                                    <Typography color="error">{error}</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && !fetchingURL && !error && catalogs.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={columns.length} align="center">
                                    No catalogs found.
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && !fetchingURL && !error && catalogs
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((row) => (
                                <TableRow
                                    key={`${row.vendorCode}-${row.companyCode}-${row.purchaseOrg}`}
                                    hover={!fetchingURL}
                                    onClick={() => handleRowClick(row)}
                                    sx={{cursor: fetchingURL ? 'default' : 'pointer'}}
                                >
                                    <TableCell>{row.companyCode}</TableCell>
                                    <TableCell>{row.purchaseOrg}</TableCell>
                                    <TableCell>{row.vendorCode}</TableCell>
                                    <TableCell>{row.vendorName}</TableCell>
                                </TableRow>
                            ))
                        }
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                rowsPerPageOptions={[]}
                component="div"
                count={catalogs.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={PaginationActions}
                labelDisplayedRows={() => ''}
                sx={{
                    '& .MuiTablePagination-toolbar': {
                        justifyContent: 'flex-end',
                        px: '24px',
                        py: '8px',
                    },
                }}
            />
            <ErrorPopup
                open={errorPopupOpen}
                onClose={() => setErrorPopupOpen(false)}
                errorMessages={[errorMessage]}
                title="PunchOut Catalog Access Error"
            />
        </>
    );
}

PunchoutCatalogTable.propTypes = {
    context: PropTypes.instanceOf(BrickContext).isRequired,
    companyCode: PropTypes.shape({label: PropTypes.string, value: PropTypes.string}),
    purchaseOrg: PropTypes.shape({label: PropTypes.string, value: PropTypes.string}),
    DYNInfo: PropTypes.object,
    PRInfo: PropTypes.object,
    EPRId: PropTypes.string,
    onClosePunchOut: PropTypes.func.isRequired,
};
